import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded'
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded'
import { useAdvancedCaseload, useRefreshAdvancedCaseload } from '../../api/AdvancedCaseloadProvider'
import { advancedColors, onDark, surfaces } from '../../theme'
import { TOUR_ANCHORS } from '../../tour/anchors'
import { usePatientHeaderSize } from '../../components/PatientHeaderSize'
import CareTeamAvatar from './CareTeamAvatar'
import CareTeamChatPicker, { CareTeamChatSelection } from './CareTeamChatPicker'
import CareTeamChatThread from './CareTeamChatThread'
import CareTeamChatParticipants from './CareTeamChatParticipants'
import UnreadCount from './UnreadCount'
import { ForwardedFeedItem } from './types'
import { useCareTeamThreads } from './useCareTeamThreads'
import { CHAT_HEADER_HEIGHT } from './ChatComposerLayout'

type Props = {
  uid: string
  open: boolean
  onOpenChange: (open: boolean) => void
  pendingForward?: ForwardedFeedItem | null
  onClearPendingForward?: () => void
  // The tour opens the chat on the list of threads, not whichever one was last open.
  showPicker?: boolean
}

// Docked beside the record; the patient page stays usable alongside it.
const FloatingCareTeamChat = ({
  uid,
  open,
  onOpenChange,
  pendingForward,
  onClearPendingForward,
  showPicker = false,
}: Props) => {
  const [selection, setSelection] = useState<CareTeamChatSelection | null>(null)
  const { height: patientHeaderHeight } = usePatientHeaderSize()
  const [width, setWidth] = useState(440)
  const [expanded, setExpanded] = useState(false)
  const [availableWidth, setAvailableWidth] = useState(1000)
  const drag = useRef<{ x: number; width: number } | null>(null)
  const dockRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const maxWidth = Math.max(340, availableWidth - 280)
  const minWidth = Math.min(340, availableWidth)
  const dockWidth = expanded || availableWidth < 680 ? '100%' : Math.min(width, maxWidth)
  const clampWidth = (next: number) => Math.max(minWidth, Math.min(maxWidth, next))

  useEffect(() => {
    const workspace = dockRef.current?.parentElement
    if (!workspace || typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const measure = () => setAvailableWidth(workspace.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(workspace)
    return () => observer.disconnect()
  }, [])
  // Held here rather than in the picker, so going back to the list never reloads it; loaded on the first open.
  const [everOpened, setEverOpened] = useState(open)
  if (open && !everOpened) {
    setEverOpened(true)
  }
  const threads = useCareTeamThreads(uid, everOpened)
  const { refresh: refreshThreads, markWrittenIn } = threads
  const caseload = useAdvancedCaseload()
  const unreadCount = (caseload.status === 'ready' && caseload.byUid[uid]?.indicators?.careTeamChatUnread?.count) || 0
  const refreshCaseload = useRefreshAdvancedCaseload()
  // The thread list and the caseload row both count this chat, so both re-read once it is marked.
  const markRead = useCallback(() => {
    refreshThreads()
    refreshCaseload()
  }, [refreshThreads, refreshCaseload])
  const reopened = useRef(false)

  useEffect(() => {
    if (!open) {
      if (reopened.current) {
        buttonRef.current?.focus()
      }
      return
    }
    panelRef.current?.focus()
    if (reopened.current) {
      refreshThreads()
    }
    reopened.current = true
  }, [open, refreshThreads])

  useEffect(() => {
    if (showPicker) {
      setSelection(null)
    }
  }, [showPicker])

  // A forward always starts on the picker, so the coach chooses who receives it.
  useEffect(() => {
    if (pendingForward) {
      setSelection(null)
    }
  }, [pendingForward])

  const writtenThread = selection?.thread

  const close = () => {
    onOpenChange(false)
  }

  return (
    <Box
      ref={dockRef}
      sx={{
        position: open ? 'relative' : 'absolute',
        top: 0,
        right: 0,
        zIndex: 3,
        boxSizing: 'border-box',
        flexShrink: 0,
        width: open ? dockWidth : 36,
        maxWidth: '100%',
        minWidth: 0,
        height: open ? '100%' : patientHeaderHeight,
        overflow: open ? 'visible' : 'hidden',
        pointerEvents: open ? 'auto' : 'none',
        borderLeft: open ? `1px solid ${surfaces.blue.stroke}` : 'none',
        backgroundColor: open ? advancedColors.surface : 'transparent',
        '&:hover .CareTeamChatLauncher, & .CareTeamChatLauncher.Mui-focusVisible': {
          transform: 'translateX(0)',
          backgroundColor: surfaces.blue.bg,
          '& .CareTeamChatLauncher-label': { opacity: 1 },
          '& .CareTeamChatLauncher-unread': { opacity: 0 },
        },
      }}
    >
      <ButtonBase
        ref={buttonRef}
        className="CareTeamChatLauncher"
        data-tour={TOUR_ANCHORS.careTeamChatButton}
        aria-label={unreadCount > 0 ? `Open care team chat, ${unreadCount} unread` : 'Open care team chat'}
        aria-expanded={open}
        aria-controls={open ? `care-team-chat-${uid}` : undefined}
        onClick={() => onOpenChange(true)}
        sx={{
          display: open ? 'none' : 'flex',
          pointerEvents: 'auto',
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          justifyContent: 'flex-start',
          borderRadius: '8px 0 0 8px',
          border: `1px solid ${surfaces.blue.stroke}`,
          borderRight: 0,
          backgroundColor: surfaces.blue.bg,
          color: surfaces.blue.fg,
          // Wider while there is something unread, so the count shows without hovering.
          transform: `translateX(calc(100% - ${unreadCount > 0 ? 28 : 8}px))`,
          transition: 'transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          overflow: 'hidden',
          '&.Mui-focusVisible': { outline: `2px solid ${surfaces.blue.fg}`, outlineOffset: -3 },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <Typography
          className="CareTeamChatLauncher-label"
          variant="body2"
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            left: 7,
            writingMode: 'vertical-rl',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            opacity: 0,
            transition: 'opacity 160ms ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          Care team chat
        </Typography>
        <Box
          className="CareTeamChatLauncher-unread"
          sx={{ position: 'absolute', top: 10, left: 3, transition: 'opacity 160ms ease' }}
        >
          <UnreadCount count={unreadCount} />
        </Box>
      </ButtonBase>

      {open && (
        <Box
          id={`care-team-chat-${uid}`}
          ref={panelRef}
          data-tour={TOUR_ANCHORS.careTeamChat}
          tabIndex={-1}
          role="dialog"
          aria-label="Care team chat"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              close()
            }
          }}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0, outline: 'none' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              height: CHAT_HEADER_HEIGHT,
              boxSizing: 'border-box',
              flexShrink: 0,
              backgroundColor: surfaces.blue.fg,
              color: onDark.primary,
            }}
          >
            <Typography component="h2" variant="h3" sx={{ flex: 1, color: 'inherit' }}>
              Care team chat
            </Typography>
            <Tooltip title={expanded ? 'Restore chat width' : 'Expand chat'}>
              <IconButton
                aria-label={expanded ? 'Restore chat width' : 'Expand chat'}
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ color: 'inherit', display: availableWidth < 680 ? 'none' : 'inline-flex' }}
              >
                {expanded ? (
                  <CloseFullscreenRoundedIcon fontSize="small" />
                ) : (
                  <OpenInFullRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close care team chat">
              <IconButton aria-label="Close care team chat" size="small" onClick={close} sx={{ color: 'inherit' }}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {selection && (
            <Box
              sx={{
                flexShrink: 0,
                borderBottom: `1px solid ${surfaces.blue.stroke}`,
                backgroundColor: advancedColors.surface,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5 }}>
                <IconButton
                  aria-label="Back to chats"
                  size="small"
                  onClick={() => setSelection(null)}
                  sx={{ color: surfaces.blue.fg }}
                >
                  <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                {selection.threadId !== null && (
                  <CareTeamAvatar name={selection.title} categoryId={selection.categoryId} size={36} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: surfaces.blue.fg, overflowWrap: 'anywhere' }}
                  >
                    {selection.title}
                  </Typography>
                  {selection.threadId !== null && (
                    <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
                      {selection.subtitle ?? 'Direct message'}
                    </Typography>
                  )}
                </Box>
                {selection.threadId === null && <CareTeamChatParticipants uid={uid} />}
              </Box>
            </Box>
          )}

          <Box sx={{ flex: 1, minHeight: 0 }}>
            {selection ? (
              <CareTeamChatThread
                uid={uid}
                threadId={selection.threadId}
                showHeader={false}
                pendingForward={pendingForward}
                onClearPendingForward={onClearPendingForward}
                onMessageSent={writtenThread ? () => markWrittenIn(writtenThread) : undefined}
                onRead={markRead}
              />
            ) : (
              <CareTeamChatPicker uid={uid} threads={threads} onSelect={setSelection} />
            )}
          </Box>
          <Box
            role="separator"
            aria-label="Resize care team chat"
            aria-orientation="vertical"
            aria-valuemin={minWidth}
            aria-valuemax={expanded ? availableWidth : Math.max(minWidth, maxWidth)}
            aria-valuenow={expanded ? availableWidth : Math.min(width, maxWidth)}
            tabIndex={0}
            title="Drag to resize · double-click to reset"
            onPointerDown={(event) => {
              if (event.button !== 0) {
                return
              }
              event.preventDefault()
              drag.current = { x: event.clientX, width: dockRef.current?.getBoundingClientRect().width ?? width }
              event.currentTarget.setPointerCapture(event.pointerId)
              setExpanded(false)
            }}
            onPointerMove={(event) => {
              if (drag.current) {
                setWidth(clampWidth(drag.current.width + drag.current.x - event.clientX))
              }
            }}
            onPointerUp={(event) => {
              drag.current = null
              event.currentTarget.releasePointerCapture(event.pointerId)
            }}
            onLostPointerCapture={() => {
              drag.current = null
            }}
            onPointerCancel={() => {
              drag.current = null
            }}
            onDoubleClick={() => {
              setExpanded(false)
              setWidth(440)
            }}
            onKeyDown={(event) => {
              if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                event.preventDefault()
                setExpanded(false)
                const step = event.shiftKey ? 80 : 24
                if (event.key === 'Home') {
                  setWidth(minWidth)
                } else if (event.key === 'End') {
                  setWidth(maxWidth)
                } else {
                  setWidth(clampWidth(Math.min(width, maxWidth) + (event.key === 'ArrowLeft' ? step : -step)))
                }
              }
            }}
            sx={{
              position: 'absolute',
              display: availableWidth < 680 ? 'none' : 'block',
              zIndex: 1,
              left: -5,
              top: 0,
              bottom: 0,
              width: 10,
              cursor: 'col-resize',
              touchAction: 'none',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 3,
                top: '50%',
                width: 4,
                height: 40,
                borderRadius: '4px',
                backgroundColor: surfaces.blue.stroke,
              },
              '&:hover::after, &:focus-visible::after': { backgroundColor: surfaces.blue.fg },
              '&:focus-visible': { outline: `2px solid ${surfaces.blue.fg}`, outlineOffset: -2 },
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export default FloatingCareTeamChat
